/**
 * The screen the app basically is.
 *
 * Everything the player sees while playing is composed here, and the composition is a
 * pure function of one `SessionView`. That is not architectural tidiness for its own
 * sake — it is what makes `/dev/states` able to render any of the fifteen states from a
 * fixture, which is the only way this UI can be worked on without an acoustic piano and
 * a quiet room.
 *
 * ## One screen, two ways in
 *
 * At the piano, chords arrive by being played and the branch cards are things to read. On
 * the Go, chords arrive from the picker, from a tapped card or from the text field, and
 * every one of them goes through `view.selectChord` — the same funnel, the same reducer,
 * the same branch pipeline. Nothing below this file knows which mode it is in except by
 * which callbacks it was handed, which is why there is no second graph, no second sheet
 * stack and no second set of fifteen states.
 *
 * ## What is deliberately absent
 *
 * No loading gate, no "please wait", no disabled state on the graph while the network is
 * busy. The recogniser and the reducer are local and synchronous; the only thing that can
 * be slow is the suggestion request, and that is confined to one column which degrades in
 * place. The player can always keep playing.
 *
 * ## Look before you leap, in the two places it was possible to leap by accident
 *
 * Both are *presentation in front of paths that already existed*, and both live here
 * because here is where the view is resolved:
 *
 *  - A branch card takes two taps — one to open it, one to take it — so this page owns
 *    which card is open. At every width: the accident it prevents (a tap that commits a
 *    chord you had not finished reading about) happened to the owner on a phone first and
 *    on a desktop second, and it was never about the size of the screen. `useNarrowLayout`
 *    survives as `stacked`, which decides only what a *shut* card shows. See `BranchCard`.
 *  - Tapping a history node opens `HistoryDetailSheet` rather than rewinding. The rewind
 *    is the sheet's primary action and is the same `view.stepBackTo` it always was.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { BranchSuggestion } from '@hc/shared';
import { hasAnyProfile } from '../audio/calibration.js';
import { CalibrationSheet } from '../components/calibration/CalibrationSheet.js';
import { GraphRail } from '../components/graph/GraphRail.js';
import { RecognitionVerdict } from '../components/graph/RecognitionVerdict.js';
import { ChordPicker } from '../components/input/ChordPicker.js';
import CommandBar from '../components/input/CommandBar.js';
import { CorrectionSheet } from '../components/sheets/CorrectionSheet.js';
import { HistoryDetailSheet } from '../components/sheets/HistoryDetailSheet.js';
import { HomeScreen } from '../components/home/HomeScreen.js';
import { AppFrame, useNarrowLayout } from '../components/system/AppFrame.js';
import { PermissionScreen } from '../components/system/PermissionScreens.js';
import { ResumeCard } from '../components/system/ResumeCard.js';
import { SaveToast } from '../components/system/SaveToast.js';
import { SessionHeader } from '../components/system/SessionHeader.js';
import { StatusChips } from '../components/system/StatusChips.js';
import { UnsupportedScreen } from '../components/system/UnsupportedScreen.js';
import { suggestionToChord } from '../components/system/chordReading.js';
import { deriveSessionPhase } from '../components/system/sessionPhase.js';
import { useCalmMotion } from '../components/system/motionPreference.js';
import { useSessionView, type SessionView } from '../components/system/sessionView.js';

export interface SessionPageProps {
  /**
   * Overrides the view from context. This is how `/dev/states` and the tests drive the
   * page from a fixture, and how a host page can inject a store without a provider.
   */
  view?: SessionView;
  /**
   * Chunk G's command bar. Still a slot, so a host can substitute one — but it now has a
   * default, because the bar has landed and an empty bottom rail was only ever a
   * placeholder for it.
   */
  commandBar?: ReactNode;
}

export function SessionPage({ view: injected, commandBar }: SessionPageProps): React.JSX.Element {
  const view = useSessionView(injected);
  const calm = useCalmMotion();
  const narrow = useNarrowLayout();
  const phase = useMemo(() => deriveSessionPhase(view), [view]);

  const [intention, setIntention] = useState(view.state.intention ?? '');
  const [correcting, setCorrecting] = useState(false);
  const [picking, setPicking] = useState(false);
  /** Which branch card is open. One at a time, at every width. */
  const [expandedBranchId, setExpandedBranchId] = useState<string | null>(null);
  /** Which history node is being looked at. A node id, so it survives the path changing. */
  const [inspectingNodeId, setInspectingNodeId] = useState<string | null>(null);
  /** Has this device ever been tuned? Unanswered — and so unoffered — until storage says. */
  const [offerCalibration, setOfferCalibration] = useState(false);
  const [calibrating, setCalibrating] = useState(false);

  const { dispatch, retryBranches, selectChord, startListening, startOnTheGo } = view;
  const onTheGo = view.mode === 'on-the-go';
  // `!== false` rather than `=== true`: a fixture-driven page that never described its
  // settings has not said the companion is off, and it renders what it always rendered.
  const aiCompanion = view.settings?.aiCompanion !== false;
  const { keyEstimate } = view.state;

  const beginSession = useCallback(async (): Promise<void> => {
    const trimmed = intention.trim();
    if (trimmed !== '') dispatch({ type: 'SET_INTENTION', intention: trimmed });
    const options = trimmed === '' ? {} : { intention: trimmed };
    // The permission request has to happen inside this call stack: it is the user gesture,
    // and it is also the only moment Safari will resume an AudioContext. On the Go there
    // is deliberately no shared path with that — see `state/sessionHost.tsx`.
    if (onTheGo) startOnTheGo?.(options);
    else await startListening?.(options);
  }, [dispatch, intention, onTheGo, startListening, startOnTheGo]);

  const handleStart = useCallback(() => {
    void beginSession();
  }, [beginSession]);

  /**
   * Calibration is the same start, with the sheet waiting behind it.
   *
   * The order matters and is the only reason this is not a button on the home screen that
   * opens a sheet directly: calibration needs the microphone, and the microphone needs
   * this tap. If the player denies it, `phase.screen` becomes `'permission'` and the sheet
   * — which is mounted only on the session screen — never appears.
   */
  const handleCalibrate = useCallback(() => {
    setOfferCalibration(false);
    void beginSession().then(() => setCalibrating(true));
  }, [beginSession]);

  // Piano only, and asked once per mount. `hasAnyProfile` answers `true` if it cannot
  // tell, so the quiet default is to say nothing.
  useEffect(() => {
    if (onTheGo) return;
    let cancelled = false;
    void hasAnyProfile().then((tuned) => {
      if (!cancelled) setOfferCalibration(!tuned);
    });
    return () => {
      cancelled = true;
    };
  }, [onTheGo]);

  /*
   * The same action the settings drawer's slider dispatches, so the home screen's "Harmony"
   * and the drawer's "Adventurousness" are one value. Only on the home screen: "New
   * session" in the header starts over without passing through it, and keeps this value
   * across (`SESSION_STARTED` carries it), so mid-session the drawer behind the gear is the
   * door, and a second slider over the graph would be one control too many.
   */
  const handleAdventurousness = useCallback(
    (value: number) => dispatch({ type: 'SET_ADVENTUROUSNESS', value }),
    [dispatch],
  );

  const handleRetryBranches = useCallback(() => {
    if (view.state.currentNodeId) retryBranches?.(view.state.currentNodeId);
  }, [retryBranches, view.state.currentNodeId]);

  /*
   * Leaving, in the two senses the header offers.
   *
   * Both clear the local intention field as well as the session, because that field is
   * seeded once from `state.intention` and would otherwise hand the next session an
   * instruction the player thought they had just thrown away.
   */
  const { endSession, newSession, saveSketch, viewSketchbook } = view;
  const handleHome = useCallback(() => {
    setIntention('');
    endSession?.();
  }, [endSession]);
  const handleNewSession = useCallback(() => {
    setIntention('');
    newSession?.();
  }, [newSession]);

  const handleSelectBranch = useCallback(
    (suggestion: BranchSuggestion) => {
      // Closed before the chord is committed, not after: the card is about to become the
      // current node through its shared layout identity, and a card mid-morph that is
      // still 240 px tall drags the whole column with it.
      setExpandedBranchId(null);
      // The chord, not the suggestion. Matching happens in the reducer against pitch
      // classes exactly as it does for a played chord, which is what makes a tapped card
      // and a played card produce the same node with the same `fromSuggestionId`.
      selectChord?.(suggestionToChord(suggestion, keyEstimate));
    },
    [keyEstimate, selectChord],
  );

  /**
   * One card open at a time, and the open one is named rather than flagged.
   *
   * Keyed on the suggestion id so that a set streaming in around an open card cannot
   * disturb it: the ids of the cards already on screen do not change as the rest arrive,
   * which is the same property the slot-based column relies on.
   */
  const handleToggleBranch = useCallback((suggestion: BranchSuggestion) => {
    setExpandedBranchId((current) => (current === suggestion.id ? null : suggestion.id));
  }, []);

  /*
   * The two stages, and the one thing about them that is still a phone's business.
   *
   * There used to be an effect here closing the open card when the window was dragged
   * wider than a phone, because above that width there was no expanded card to be. Now
   * both layouts have a place for one — the phone gives it the column, everything else
   * gives it most of the column and dims the rest — so a card open across a resize simply
   * changes shape, and there is nothing to clean up.
   */
  const branchExpansion = useMemo(
    () => ({
      expandedId: expandedBranchId,
      onToggle: handleToggleBranch,
      stacked: narrow,
      fetchExplanation: view.fetchExplanation,
      keyEstimate,
      // The card plays the chord it already holds — the same `suggestionToChord` respelling
      // `handleSelectBranch` commits, resolved once by `GraphRail`'s cache — so what the
      // player auditions and what they take cannot drift apart. Absent at the piano, and
      // the card then draws no button at all.
      onPreview: view.previewChord,
    }),
    [
      narrow,
      expandedBranchId,
      handleToggleBranch,
      view.fetchExplanation,
      view.previewChord,
      keyEstimate,
    ],
  );

  /**
   * The history node being looked at, resolved from the live path every render.
   *
   * Resolving rather than storing the node means the sheet cannot show a chord that has
   * since been corrected or rewound away: the moment the path stops containing that id,
   * this is null and the sheet is closed.
   */
  const inspecting = useMemo(
    () => view.state.path.find((node) => node.id === inspectingNodeId) ?? null,
    [inspectingNodeId, view.state.path],
  );

  const handleExploreFrom = useCallback(
    (nodeId: string) => {
      setInspectingNodeId(null);
      view.stepBackTo?.(nodeId);
    },
    [view],
  );

  // The picker opens itself for the first chord, because at that moment it is the only
  // thing on screen worth doing. It does not reopen on close: the flag it watches has not
  // changed, and re-presenting a sheet somebody just dismissed is the sort of thing that
  // makes an app feel like it is arguing.
  useEffect(() => {
    if (phase.chooseFirstChord) setPicking(true);
  }, [phase.chooseFirstChord]);

  /**
   * Recognition collection: the ✓/✗ offer beside the current chord.
   *
   * Piano only, because On the Go has no recognitions to have an opinion about, and
   * mounted at all only when the setting is on — off, `GraphRail` is handed nothing and
   * the graph is byte for byte what it was. `getParams` is the live recogniser's, so a
   * record carries the parameters that produced it rather than the shipped defaults.
   */
  const recognizer = view.recognizer;
  const collecting = (view.settings?.collectRecognitions ?? false) && !onTheGo;
  const verdictChrome = collecting ? (
    <RecognitionVerdict
      node={phase.current}
      active={collecting}
      sessionId={view.state.sessionId}
      calm={calm}
      {...(recognizer ? { getParams: () => recognizer.getParams() } : {})}
      onCorrect={() => setCorrecting(true)}
    />
  ) : undefined;

  // The resume card and the save toast sit over whatever screen is showing, so they are
  // rendered once, outside the screen switch.
  const overlays = (
    <>
      {phase.showResumeCard && view.resumeSnapshot ? (
        <div className="hc-overlay">
          <ResumeCard
            snapshot={view.resumeSnapshot}
            onResume={() => view.resumeSession?.()}
            onStartFresh={() => view.startFresh?.()}
          />
        </div>
      ) : null}
      <SaveToast
        receipt={view.lastSave ?? null}
        onDismiss={view.dismissSave}
        onRename={
          view.renameSketch
            ? (sketchId) => view.renameSketch?.(sketchId, view.lastSave?.title ?? '')
            : undefined
        }
        onView={view.viewSketch}
      />
    </>
  );

  if (phase.screen === 'unsupported' && view.capabilities) {
    return (
      <AppFrame className="hc-app--screen">
        <UnsupportedScreen report={view.capabilities} view={view} />
        {overlays}
      </AppFrame>
    );
  }

  if (phase.screen === 'permission') {
    return (
      <AppFrame className="hc-app--screen">
        <PermissionScreen view={view} onRetry={handleStart} />
        {overlays}
      </AppFrame>
    );
  }

  if (phase.screen === 'home') {
    return (
      <AppFrame className="hc-app--screen">
        {/*
          The intention field only where something reads what is written in it — with the
          AI companion off there is nothing on the other end of it. See `HomeScreen`.
        */}
        <HomeScreen
          {...(aiCompanion ? { intention, onIntentionChange: setIntention } : {})}
          onStart={handleStart}
          starting={view.state.listening === 'awaiting-first-chord'}
          mode={view.mode ?? 'piano'}
          {...(view.setMode ? { onModeChange: view.setMode } : {})}
          {...(view.viewSketchbook ? { onOpenSketchbook: view.viewSketchbook } : {})}
          {...(offerCalibration && !onTheGo && startListening
            ? { onCalibrate: handleCalibrate }
            : {})}
          pianoAvailable={view.capabilities?.supported ?? true}
          adventurousness={view.state.adventurousness}
          onAdventurousnessChange={handleAdventurousness}
        />
        {overlays}
      </AppFrame>
    );
  }

  return (
    /*
     * `hc-app--stacked` is not passed in: `AppFrame` stamps it from the same width this page
     * reads for `narrow`, on every screen rather than on this one. It used to be added here,
     * for one job — the two strips that float over the graph become rows on a phone, because
     * four nav controls and two status chips do not fit on one line at 390 or at 517 and
     * until then they simply painted over each other. That job is still done (see
     * `graph.css`); it is just no longer the only one the class has.
     */
    <AppFrame className="hc-app--session">
      <StatusChips
        state={view.state}
        companionOffline={phase.companionOffline}
        mode={view.mode ?? 'piano'}
        storageUnavailable={view.capabilities ? !view.capabilities.canSave : false}
        onStartListening={view.startListening ? () => void view.startListening?.({}) : undefined}
      />

      <SessionHeader
        hasWork={view.state.path.length > 0}
        {...(endSession ? { onHome: handleHome } : {})}
        {...(newSession ? { onNewSession: handleNewSession } : {})}
        {...(viewSketchbook ? { onOpenSketchbook: viewSketchbook } : {})}
        {...(saveSketch ? { onSave: () => saveSketch() } : {})}
      />

      <main className="hc-session" data-testid="hc-session">
        <GraphRail
          state={view.state}
          phase={phase}
          calm={calm}
          onOpenCorrection={() => setCorrecting(true)}
          onRetryBranches={handleRetryBranches}
          {...(onTheGo && selectChord ? { onSelectBranch: handleSelectBranch } : {})}
          {...(onTheGo && selectChord ? { onOpenPicker: () => setPicking(true) } : {})}
          branchExpansion={branchExpansion}
          {...(view.stepBackTo ? { onOpenHistory: setInspectingNodeId } : {})}
          {...(verdictChrome ? { currentNodeChrome: verdictChrome } : {})}
        />
      </main>

      {/*
        The command bar. Handed the same view this page resolved rather than left to read
        context for itself, so that a fixture-driven page — `/dev/states`, a test — gets a
        bar wired to the session on screen instead of to the idle one behind it.
      */}
      <div className="hc-commandbar" data-slot="command-bar">
        {commandBar ?? <CommandBar view={view} />}
      </div>

      <CorrectionSheet
        open={correcting}
        node={phase.current}
        keyEstimate={keyEstimate}
        onClose={() => setCorrecting(false)}
        dispatch={dispatch}
      />

      {/*
        Mounted only where something can open it. At the piano the input device is the
        piano; a picker reachable from the session screen would quietly become the faster
        way to use the app, which is the thing the whole graph is designed against.
      */}
      {onTheGo && selectChord ? (
        <ChordPicker
          open={picking}
          onClose={() => setPicking(false)}
          onSelect={selectChord}
          keyContext={keyEstimate}
          path={view.state.path}
          title={phase.chooseFirstChord ? 'Choose your first chord' : 'Any chord'}
        />
      ) : null}

      {/*
        The first run, opened by the home screen's one quiet line and mounted only here —
        after `startListening` resolved, so the microphone it is about to measure exists.
        Piano only: On the Go has no recogniser to calibrate.
      */}
      {calibrating && !onTheGo && view.recognizer ? (
        <CalibrationSheet open onClose={() => setCalibrating(false)} recognizer={view.recognizer} />
      ) : null}

      {/*
        Mounted only where the rewind is wired, which is the same condition that makes a
        history node tappable at all — a detail sheet whose primary action does nothing
        would be a preview of a door that is not there.
      */}
      {view.stepBackTo ? (
        <HistoryDetailSheet
          open={inspecting !== null}
          node={inspecting}
          state={view.state}
          fetchExplanation={view.fetchExplanation}
          onExplore={handleExploreFrom}
          {...(view.previewChord ? { onPreview: view.previewChord } : {})}
          {...(saveSketch ? { onSave: () => saveSketch() } : {})}
          onClose={() => setInspectingNodeId(null)}
        />
      ) : null}

      {overlays}
    </AppFrame>
  );
}

export default SessionPage;
